import React, { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { FileCheck2, Printer, Loader2, AlertTriangle, ShieldCheck, SprayCan, Radar, ClipboardList } from 'lucide-react';
import { audit, apiCustomers } from '../../lib/api';
import { printElement } from '../../lib/importExport';
import { toast } from 'sonner';
import { shiftDays, todayISO } from '../../lib/dates';

const num = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 3 });
const actColor = (a) => ({ None: 'bg-slate-100 text-slate-600', Low: 'bg-blue-100 text-blue-700', Moderate: 'bg-amber-100 text-amber-700', High: 'bg-red-100 text-red-700' }[a] || 'bg-slate-100 text-slate-600');

export default function AuditPackPage() {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [start, setStart] = useState(shiftDays(todayISO(), -90));
  const [end, setEnd] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { apiCustomers.list().then((c) => setCustomers(Array.isArray(c) ? c : [])).catch(() => {}); }, []);

  const generate = async () => {
    if (!customerId) { toast.error('Select a site first'); return; }
    setLoading(true);
    try { const d = await audit.pack({ customerId, start, end }); setData(d); }
    catch (e) { toast.error('Could not generate the audit pack'); }
    finally { setLoading(false); }
  };

  const dv = data?.devices || { counts: { total: 0, active: 0, byActivity: {} }, attention: [], list: [] };
  const px = data?.pesticide || { summary: [], log: [], totalApplications: 0 };
  const svc = data?.services || [];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-2">
        <FileCheck2 className="w-6 h-6 text-[#8BC53F]" />
        <h1 className="text-2xl font-extrabold text-slate-900">FDA / IPM Audit Pack</h1>
      </div>
      <p className="text-slate-500 text-sm mt-1">Generate a single, dated, printable packet per site - device activity, pesticide use, and service history - ready to hand an inspector.</p>

      <Card className="mt-4 p-4 border-slate-200">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-xs font-medium text-slate-600">Site / Customer</label>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 bg-white text-sm" data-testid="audit-customer">
              <option value="">- select a site -</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.customerName || c.id}</option>)}
            </select>
          </div>
          <div><label className="text-xs font-medium text-slate-600">From</label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10" /></div>
          <div><label className="text-xs font-medium text-slate-600">To</label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10" /></div>
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={generate} disabled={loading} className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold gap-1.5" data-testid="audit-generate">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />}{loading ? 'Generating...' : 'Generate'}
          </Button>
          {data && <Button onClick={() => printElement('#audit-doc')} variant="outline" className="gap-1.5"><Printer className="w-4 h-4" />Print / Save PDF</Button>}
        </div>
      </Card>

      {loading && !data ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="w-7 h-7 animate-spin mx-auto" /></div>
      ) : !data ? (
        <p className="mt-10 text-center text-slate-400 text-sm">Pick a site and date range, then Generate.</p>
      ) : (
        <div id="audit-doc" className="mt-5 bg-white">
          <div className="border-2 border-slate-900 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xl font-extrabold text-slate-900">{data.operator.name || 'Xpertpests Solutions'}</div>
                <div className="text-xs text-slate-500 mt-0.5">{data.operator.address}</div>
                <div className="text-xs text-slate-500">{data.operator.phone}{data.operator.email ? ' - ' + data.operator.email : ''}</div>
                {data.operator.license ? <div className="text-xs text-slate-500">PCO License: {data.operator.license}</div> : null}
              </div>
              <div className="text-right">
                <div className="inline-flex items-center gap-1.5 text-[#6FA82C] font-extrabold tracking-wide"><ShieldCheck className="w-5 h-5" />IPM / FDA AUDIT REPORT</div>
                <div className="text-xs text-slate-500 mt-1">Period: {data.range.from} to {data.range.to}</div>
                <div className="text-xs text-slate-400">Generated: {String(data.generatedAt).slice(0, 10)}</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-200">
              <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Site</div>
              <div className="font-bold text-slate-900">{data.site.name || '(unnamed site)'}</div>
              <div className="text-sm text-slate-500">{data.site.address}</div>
            </div>
          </div>

          <div className="mt-5">
            <div className="flex items-center gap-2 mb-2"><Radar className="w-4 h-4 text-[#6FA82C]" /><h2 className="font-extrabold text-slate-900">1. Device Activity</h2></div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <div className="rounded-lg border border-slate-200 p-2 text-center"><div className="text-[10px] text-slate-400 uppercase">Total</div><div className="text-lg font-extrabold text-slate-900">{dv.counts.total}</div></div>
              <div className="rounded-lg border border-slate-200 p-2 text-center"><div className="text-[10px] text-slate-400 uppercase">Active</div><div className="text-lg font-extrabold text-slate-900">{dv.counts.active}</div></div>
              <div className="rounded-lg border border-slate-200 p-2 text-center"><div className="text-[10px] text-slate-400 uppercase">None</div><div className="text-lg font-extrabold text-slate-600">{dv.counts.byActivity.None || 0}</div></div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-2 text-center"><div className="text-[10px] text-blue-500 uppercase">Low</div><div className="text-lg font-extrabold text-blue-700">{dv.counts.byActivity.Low || 0}</div></div>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-center"><div className="text-[10px] text-amber-500 uppercase">Mod</div><div className="text-lg font-extrabold text-amber-700">{dv.counts.byActivity.Moderate || 0}</div></div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-2 text-center"><div className="text-[10px] text-red-500 uppercase">High</div><div className="text-lg font-extrabold text-red-700">{dv.counts.byActivity.High || 0}</div></div>
            </div>
            {dv.attention.length > 0 && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span><b>Needs attention:</b> {dv.attention.map((d) => d.label + ' (' + d.lastActivity + ')').join(', ')}</span>
              </div>
            )}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-400 border-b border-slate-200">
                  <th className="py-1.5 pr-2 font-semibold">Device</th><th className="py-1.5 pr-2 font-semibold">Type</th><th className="py-1.5 pr-2 font-semibold">Location</th><th className="py-1.5 pr-2 font-semibold">Activity</th><th className="py-1.5 pr-2 font-semibold">Condition</th><th className="py-1.5 pr-2 font-semibold">Last Insp.</th>
                </tr></thead>
                <tbody>
                  {dv.list.map((d, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-1.5 pr-2 font-medium text-slate-800">{d.label}</td>
                      <td className="py-1.5 pr-2 text-slate-600">{d.type}</td>
                      <td className="py-1.5 pr-2 text-slate-600">{d.location || '-'}</td>
                      <td className="py-1.5 pr-2"><span className={`inline-block px-1.5 py-0.5 rounded ${actColor(d.lastActivity)}`}>{d.lastActivity}</span></td>
                      <td className="py-1.5 pr-2 text-slate-600">{d.lastCondition}</td>
                      <td className="py-1.5 pr-2 text-slate-500">{d.lastInspected || '-'}</td>
                    </tr>
                  ))}
                  {dv.list.length === 0 && <tr><td colSpan={6} className="py-4 text-center text-slate-400">No devices on file for this site.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2"><SprayCan className="w-4 h-4 text-[#6FA82C]" /><h2 className="font-extrabold text-slate-900">2. Pesticide Use <span className="text-sm font-normal text-slate-400">({px.totalApplications} application{px.totalApplications === 1 ? '' : 's'})</span></h2></div>
            {px.summary.length === 0 ? (
              <p className="text-sm text-slate-400">No pesticide applications recorded in this period.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-slate-400 border-b border-slate-200">
                      <th className="py-1.5 pr-2 font-semibold">Product</th><th className="py-1.5 pr-2 font-semibold">EPA Reg No.</th><th className="py-1.5 pr-2 font-semibold">Active Ingredient</th><th className="py-1.5 pr-2 font-semibold text-right">Total Used</th>
                    </tr></thead>
                    <tbody>
                      {px.summary.map((r, i) => (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-1.5 pr-2 font-medium text-slate-800">{r.productName}</td>
                          <td className="py-1.5 pr-2 text-slate-600">{r.epaRegNo || '-'}</td>
                          <td className="py-1.5 pr-2 text-slate-600">{r.activeIngredient || '-'}</td>
                          <td className="py-1.5 pr-2 text-right font-bold tabular-nums">{num(r.totalAmount)} {r.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {px.log.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-[#6FA82C] font-semibold cursor-pointer">Detailed application log ({px.log.length})</summary>
                    <div className="mt-2 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead><tr className="text-left text-slate-400 border-b border-slate-200">
                          <th className="py-1 pr-2 font-semibold">Date</th><th className="py-1 pr-2 font-semibold">Product</th><th className="py-1 pr-2 font-semibold">Amount</th><th className="py-1 pr-2 font-semibold">Target</th><th className="py-1 pr-2 font-semibold">Applicator</th>
                        </tr></thead>
                        <tbody>
                          {px.log.map((a, i) => (
                            <tr key={i} className="border-b border-slate-50">
                              <td className="py-1 pr-2 text-slate-500">{a.date}</td>
                              <td className="py-1 pr-2 text-slate-700">{a.productName}</td>
                              <td className="py-1 pr-2 tabular-nums">{num(a.amount)} {a.unit}</td>
                              <td className="py-1 pr-2 text-slate-600">{a.targetPest || '-'}</td>
                              <td className="py-1 pr-2 text-slate-600">{a.applicatorName || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </>
            )}
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2"><ClipboardList className="w-4 h-4 text-[#6FA82C]" /><h2 className="font-extrabold text-slate-900">3. Service History</h2></div>
            {svc.length === 0 ? (
              <p className="text-sm text-slate-400">No services recorded in this period.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-left text-slate-400 border-b border-slate-200">
                    <th className="py-1.5 pr-2 font-semibold">Date</th><th className="py-1.5 pr-2 font-semibold">Service</th><th className="py-1.5 pr-2 font-semibold">Status</th><th className="py-1.5 pr-2 font-semibold">Technician</th><th className="py-1.5 pr-2 font-semibold">Notes</th>
                  </tr></thead>
                  <tbody>
                    {svc.map((s, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="py-1.5 pr-2 text-slate-500 whitespace-nowrap">{s.date}</td>
                        <td className="py-1.5 pr-2 font-medium text-slate-800">{s.service}</td>
                        <td className="py-1.5 pr-2 text-slate-600">{s.status}</td>
                        <td className="py-1.5 pr-2 text-slate-600">{s.tech || '-'}</td>
                        <td className="py-1.5 pr-2 text-slate-600">{s.notes || s.targetPests || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500">
            <p>This report was generated from service records maintained by {data.operator.name || 'the operator'}. Records are retained per California DPR requirements.</p>
            <div className="mt-6 grid grid-cols-2 gap-6">
              <div><div className="border-b border-slate-400 h-6"></div><div className="mt-1">Operator / Technician signature</div></div>
              <div><div className="border-b border-slate-400 h-6"></div><div className="mt-1">Date</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
